import React from "react";
import { Workspaces, Badge, DateRange } from "@mui/icons-material";
import moment from "moment";
import {
  correctionMeta,
  statusMeta,
  canonicalStatus,
  derivePairFromEntries,
  normalizePunchEntries,
  inferCorrectionType,
  toIsoRequestDate,
  correctionRowId,
} from "pages/MyZone/MyZoneApply/AttendanceCorrection/attendanceCorrectionHelpers";

// Reuses My Zone's correction-type taxonomy (label/color/icon per type),
// status helpers, and entries->punch-pair collapse, so a "Missing Punch (In)"
// chip or a "Rejected" status reads identically whether it's seen from the
// employee's My Requests screen or here on the approver's queue — one source
// of truth for both sides of the same request type.
export { correctionMeta, statusMeta, canonicalStatus };

// Backend endpoint (confirmed):
//   GET  team_zone/approve/attendance_correction/teamzone-attendance-correction/
//        ?user_id=<manager id>&company_id=<id>&start_date=&end_date=
//   PUT  team_zone/approve/attendance_correction/teamzone-attendance-correction/
//        { user_id, date, status: "Approved", early_exit: false }
//        { user_id, date, status: "Rejected", rejected_reason }
// GET params mirror every other Team Zone Approve screen (Break/ShiftCorrection/
// Permission/CompOff) rather than the `status=` example — fetching the whole
// date range once and filtering by status client-side is what lets the
// Pending/Approved/Rejected/Cancelled cards below and the CustomFilter table
// share a single fetch, same as those screens.
// PUT is per (user_id, date) — one attendance record, not a bulk `ids` list —
// so bulk approve/reject in index.js fires one PUT per selected row.
// `early_exit` has no source field on the GET row today; it's sent `false`
// on every approve until product defines when it should flip.
export const ATTENDANCE_CORRECTION_API =
  "team_zone/approve/attendance_correction/teamzone-attendance-correction/";

// Team Zone's own column set (Name/Employee ID ahead of the request columns
// My Zone already defined).
export const ATTENDANCE_CORRECTION_HEADERS = [
  {
    key: "employee_name",
    name: "Name",
    type: "string",
    icon: <Workspaces fontSize="small" />,
  },
  {
    key: "employee_id",
    name: "Employee ID",
    type: "string",
    icon: <Badge fontSize="small" />,
  },
  {
    key: "date_display",
    name: "Date",
    type: "date",
    icon: <DateRange fontSize="small" />,
  },
  { key: "correction_label", name: "Correction Type", type: "autocomplete" },
  { key: "original_display", name: "Original Punch", type: "string" },
  { key: "corrected_display", name: "Corrected Punch", type: "string" },
  { key: "approver_name", name: "Approver", type: "string" },
  { key: "status", name: "Status", type: "autocomplete" },
];

const punchText = (inVal, outVal) =>
  !inVal && !outVal ? "--" : `In: ${inVal || "--"} Out: ${outVal || "--"}`;

// Normalizes a record for CustomFilter's search/sort and the table — every
// visible column must address a flat row[key].
export const toApproveFilterRow = (r) => ({
  ...r,
  correction_label: correctionMeta(r.correction_type).label,
  date_display: r.date ? moment(r.date).format("DD-MM-YYYY") : "",
  original_display: punchText(r.original_in, r.original_out),
  corrected_display: punchText(r.corrected_in, r.corrected_out),
  // From the API: the assigned approver while Pending, else whoever approved
  // or rejected it. Never the person who cancelled it.
  approver_name: r.approver_name || "-",
});

// Same "DD-MM-YYYY, HH:mm" tolerance as shiftCorrectionApproveHelpers'
// formatAppliedDateTime — team_zone approve endpoints share that convention
// for applied_on/approved_at/rejected_at.
export const formatAppliedDateTime = (value) => {
  if (!value) return "";
  const parsed = moment(value, "DD-MM-YYYY, HH:mm", true);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY, HH:mm") : value;
};

const entriesFromPair = (inValue, outValue, fallbackDate) =>
  [
    inValue
      ? { time: inValue, direction: "IN", date: fallbackDate }
      : null,
    outValue
      ? { time: outValue, direction: "OUT", date: fallbackDate }
      : null,
  ].filter(Boolean);

const pickEntries = (row, keys, fallbackDate, pairKeys = []) => {
  for (const key of keys) {
    const normalized = normalizePunchEntries(row?.[key], fallbackDate);
    if (normalized.length) return normalized;
  }

  for (const [inKey, outKey] of pairKeys) {
    const normalized = normalizePunchEntries(
      entriesFromPair(row?.[inKey], row?.[outKey], fallbackDate),
      fallbackDate,
    );
    if (normalized.length) return normalized;
  }

  return [];
};

// Maps one row from the GET response into the flat record shape the status
// cards / table / CustomFilter / detail panel already consume. `id` is the same
// unique user+date+status key My Zone's mapCorrectionApiRow uses -- keying on
// attendance_record_id gave two requests for the same day the same React key,
// which rendered ghost/duplicate rows after search/sort/filter.
export const toApproveTeamCorrectionRecord = (row = {}, index = 0) => {
  const rawDate = toIsoRequestDate(row.raw_date) || toIsoRequestDate(row.date);
  const entries = pickEntries(
    row,
    ["entries", "requested_entries", "corrected_entries", "punch_entries"],
    rawDate,
    [
      ["corrected_in", "corrected_out"],
      ["requested_in", "requested_out"],
    ],
  );
  const originalEntries = pickEntries(
    row,
    ["original_entries", "current_entries", "current_punches", "current_punch_entries"],
    rawDate,
    [
      ["original_in", "original_out"],
      ["current_in", "current_out"],
      ["actual_in", "actual_out"],
    ],
  );
  const requestedPair = derivePairFromEntries(entries);
  const originalPair = derivePairFromEntries(originalEntries);

  return {
    id: correctionRowId(row, rawDate, index),
    request_id: rawDate ? `ACR-${rawDate}` : `ACR-${index}`,
    user_id: row.user_id,
    employee_name: row.employee_name,
    employee_id: row.employee_id,
    employee_code: row.employee_code,
    department: row.department,
    designation: row.designation,
    image_url: row.image_url,
    color_code: row.color_code,
    // PUT needs this back as the ISO raw_date — keep it as the canonical
    // `date` field (display formatting happens downstream via date_display).
    date: rawDate,
    correction_type:
      row.correction_type !== undefined
        ? row.correction_type
        : inferCorrectionType(entries, originalEntries),
    original_in: originalPair.in,
    original_out: originalPair.out,
    corrected_in: requestedPair.in,
    corrected_out: requestedPair.out,
    entries,
    original_entries: originalEntries,
    early_exit: Boolean(row.early_exit),
    attendance_record_id: row.attendance_record_id ?? null,
    reason: row.reason || "",
    status: canonicalStatus(row.status) || "Pending",
    applied_by: row.applied_by || row.employee_name,
    // The backend sends these as "DD-MM-YYYY, HH:mm" -- format them here so
    // every consumer (detail panel timeline, expand-row status flow) shows
    // the same "DD-MM-YYYY, HH:mm" style My Zone's own view uses,
    // instead of leaking the raw backend string.
    applied_on: formatAppliedDateTime(row.applied_on),
    approved_by: row.approved_by,
    approved_at: formatAppliedDateTime(row.approved_at),
    rejected_by: row.rejected_by,
    rejected_at: formatAppliedDateTime(row.rejected_at),
    rejected_reason: row.rejected_reason,
    // Previously missing entirely, so a cancelled request's expand-row status
    // flow and detail-panel timeline had nothing to show under the Cancelled
    // step even though the backend already returns these fields.
    cancelled_by: row.cancelled_by,
    cancelled_at: formatAppliedDateTime(row.cancelled_at),
    cancelled_reason: row.cancelled_reason,
    approver_name: row.approver_name,
    edit_audit_trail: row.edit_audit_trail || [],
  };
};
